import React, { useState, useEffect } from 'react';
import { IndianRupee, ArrowLeft, Calendar, Wallet, X } from 'lucide-react';

interface CollectionRecord {
  date: string;
  time: string;
  mode: string;
  amount: string;
}

interface Customer {
  id: number;
  name: string;
  number: string | null;
  history: CollectionRecord[];
}

const collectionsData: Customer[] = [
  { id: 1, name: "FEBIN 1", number: "8075247562", history: [] },
  { id: 2, name: "FEBIN 2", number: null, history: [] },
  { id: 3, name: "FEBIN 3", number: null, history: [] },
  { id: 4, name: "FEBIN 4", number: null, history: [] },
  { id: 5, name: "SAHID", number: null, history: [] },
  { id: 6, name: "JAVED", number: "7902455547", history: [] },
  { id: 7, name: "ASIF", number: "6325699390", history: [] },
  { id: 8, name: "MANOHAR", number: "9959493023", history: [] },
  { id: 9, name: "JAI SANKAR", number: null, history: [] },
  { id: 10, name: "MANOHAR - SIVA", number: null, history: [] },
  { id: 11, name: "MANOHAR RAJENDRA", number: null, history: [] },
  { id: 12, name: "VENKATESH - WATER", number: null, history: [] },
  { id: 13, name: "YS MANI", number: "8977961210", history: [] },
  { id: 14, name: "POLIAN", number: "9398990801", history: [] },
  { id: 15, name: "BRAMHIAH AC", number: "9502008211", history: [] },
  { id: 16, name: "SURYA NARAYANA", number: "8686688777", history: [] },
  { id: 17, name: "KIRAN", number: "9908842557", history: [] },
  { id: 18, name: "HUSSAIN", number: "8897441180", history: [] },
  { id: 19, name: "ABDULLA", number: "8073805648", history: [] },
  { id: 20, name: "RAVI MUNI", number: null, history: [] },
  { id: 21, name: "RAVI - JAVED", number: null, history: [] },
  { id: 22, name: "RAVI", number: null, history: [] },
  { id: 23, name: "RAVI - YS MANI", number: null, history: [] },
  { id: 24, name: "FEBIN - YS MANI", number: null, history: [] },
  { id: 25, name: "MURALI", number: null, history: [] },
  { id: 26, name: "IZAZ", number: null, history: [] },
  { id: 27, name: "RIYAZ", number: null, history: [] },
  { id: 28, name: "SONU", number: null, history: [] },
  { id: 29, name: "Hussain - Manikanta", number: null, history: [] },
  { id: 30, name: "Manohar Guru", number: null, history: [] }
];

function App() {
  const [collections, setCollections] = useState<Customer[]>(collectionsData);
  const [inputValues, setInputValues] = useState<{[key: number]: string}>({});
  const [selectedMode, setSelectedMode] = useState<{[key: number]: string}>({});
  const [currentPage, setCurrentPage] = useState<'list' | 'history'>('list');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalCustomer, setModalCustomer] = useState<Customer | null>(null);
  const [todayTotal, setTodayTotal] = useState(0);

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const getTodayTotal = () => {
    const today = new Date().toLocaleDateString('en-IN');
    let total = 0;
    collections.forEach(customer => {
      customer.history.forEach(record => {
        if (record.date === today) {
          total += parseFloat(record.amount) || 0;
        }
      });
    });
    return total;
  };

  useEffect(() => {
    setTodayTotal(getTodayTotal());
  }, [collections]);

  const handleAmountChange = (customerId: number, value: string) => {
    setInputValues(prev => ({
      ...prev,
      [customerId]: value
    }));
  };

  const handleModeSelect = (customerId: number, mode: string) => {
    setSelectedMode(prev => ({
      ...prev,
      [customerId]: mode
    }));
  };

  const handleMarkCollected = (customerId: number) => {
    const amount = inputValues[customerId];
    const mode = selectedMode[customerId];
    
    if (!amount || !mode) return;

    const today = new Date().toLocaleDateString('en-IN');
    const timestamp = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    
    setCollections(prev => prev.map(c => 
      c.id === customerId 
        ? { 
            ...c, 
            history: [...c.history, { date: today, time: timestamp, mode: mode, amount: amount }]
          }
        : c
    ));
    
    // Clear inputs after marking
    setInputValues(prev => {
      const newState = { ...prev };
      delete newState[customerId];
      return newState;
    });
    setSelectedMode(prev => {
      const newState = { ...prev };
      delete newState[customerId];
      return newState;
    });
  };

  const handleViewHistory = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCurrentPage('history');
  }; // This function is kept for potential future use

  const handleBack = () => {
    setCurrentPage('list');
    setSelectedCustomer(null);
  };

  // Removed unused handleUpdateClick to satisfy TypeScript build

  const handleModalClose = () => {
    setShowModal(false);
    setModalCustomer(null);
  };

  const handleModalSubmit = (amount: string, mode: string) => {
    if (!amount || !mode || !modalCustomer) return;

    const today = new Date().toLocaleDateString('en-IN');
    const timestamp = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    
    setCollections(prev => prev.map(c => 
      c.id === modalCustomer.id 
        ? { 
            ...c, 
            history: [...c.history, { date: today, time: timestamp, mode: mode, amount: amount }]
          }
        : c
    ));
    
    handleModalClose();
  };

  const getTodayCollection = (customer: Customer) => {
    const today = new Date().toLocaleDateString('en-IN');
    return customer.history.find(h => h.date === today);
  };

  const getTotalCollection = (customer: Customer) => {
    return customer.history.reduce((sum, h) => sum + parseFloat(h.amount || '0'), 0);
  };

  const getLastFiveCollections = (customer: Customer) => {
    return customer.history.slice(-5).reverse();
  };

  // List Page
  if (currentPage === 'list') {
    return (
      <div className="min-h-screen bg-black text-white font-sans">
        {/* Header Row: Date and Today's Total in one line */}
        <div className="bg-black border-b border-white/15 px-4 py-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-lg md:text-xl font-semibold text-white px-2 md:px-4">
              {today}
            </div>
            <div className="text-lg md:text-xl font-semibold text-white px-2 md:px-4">
              ₹ Today’s Total: ₹{todayTotal.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Collection List */}
        <div className="p-4 md:p-6">
          {/* Single-column on mobile/tablet; more columns on large desktops */}
          <div className="grid grid-cols-1 gap-3 md:gap-4 xl:grid-cols-2 2xl:grid-cols-3">
          {collections.map((customer) => {
            const todayCollection = getTodayCollection(customer);
            const lastFiveCollections = getLastFiveCollections(customer);
            const amtStr = inputValues[customer.id] || '';
            const amt = parseFloat(amtStr);
            const canCollect = !isNaN(amt) && amt > 0;
            
            return (
              <div
                key={customer.id}
                className="w-full max-w-full rounded-xl border border-white/15 bg-black p-3 md:p-4 space-y-2 md:space-y-3"
              >
                {/* Top row: Name (left) + Payment buttons (right) */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button
                    onClick={() => handleViewHistory(customer)}
                    className="text-white font-semibold text-left"
                  >
                    {customer.name}
                  </button>
                  {!todayCollection && (
                    <div className="flex flex-wrap items-center gap-2">
                      {['Cash', 'UPI', 'PP'].map((mode) => (
                        <button
                          key={mode}
                          onClick={() => handleModeSelect(customer.id, mode)}
                          className={`inline-flex items-center justify-center h-9 px-4 rounded-lg border border-white transition leading-none whitespace-nowrap focus:outline-none ${
                            selectedMode[customer.id] === mode
                              ? 'bg-white text-black'
                              : 'bg-black text-white hover:bg-white hover:text-black'
                          }`}
                        >
                          {mode}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Phone number */}
                {customer.number && (
                  <p className="text-white/70 text-sm mb-1">{customer.number}</p>
                )}

                {/* If already collected today, show the indicator and skip input */}
                {todayCollection && (
                  <div className="mb-1">
                    <span className="text-white text-base font-medium">
                      ✓ ₹{todayCollection.amount} • {todayCollection.mode} • {todayCollection.time}
                    </span>
                  </div>
                )}

                {/* Amount Input and Payment Mode */}
                {!todayCollection && (
                  <div className="flex flex-wrap md:flex-nowrap items-center gap-3 mt-2">
                    {/* Amount Input */}
                    <input
                      type="number"
                      placeholder="Enter amount"
                      value={inputValues[customer.id] || ''}
                      onChange={(e) => handleAmountChange(customer.id, e.target.value)}
                      className="flex-1 min-w-0 h-11 bg-black text-white placeholder-white/60 border border-white/20 rounded-lg px-4 outline-none"
                    />

                    {/* Collected Button */}
                    <button
                      onClick={() => handleMarkCollected(customer.id)}
                      disabled={!canCollect}
                      className={`inline-flex items-center justify-center leading-none select-none h-11 px-5 rounded-lg border transition whitespace-nowrap ${
                        canCollect
                          ? 'bg-white text-black border-white hover:bg-black hover:text-white'
                          : 'border-white/30 text-white/50 bg-black cursor-not-allowed'
                      }`}
                    >
                      Collected
                    </button>
                  </div>
                )}

                {/* Collection History Table */}
                {lastFiveCollections.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-base font-bold text-white mb-2">Recent Collections</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-white/70 border-b border-[#333]">
                            <th className="text-left py-2 text-base">Date</th>
                            <th className="text-left py-2 text-base">Mode</th>
                            <th className="text-right py-2 text-base">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lastFiveCollections.map((record, index) => (
                            <tr key={index} className="text-white border-b border-[#333]">
                              <td className="py-2 text-base">{record.date}</td>
                              <td className="py-2 text-base">{record.mode}</td>
                              <td className="py-2 font-semibold text-base text-right">₹{record.amount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>

        {/* Update Modal */}
        {showModal && modalCustomer && (
          <UpdateModal
            customer={modalCustomer}
            onClose={handleModalClose}
            onSubmit={handleModalSubmit}
          />
        )}
      </div>
    );
  }

  // History Page
  if (currentPage === 'history' && selectedCustomer) {
    const totalAmount = getTotalCollection(selectedCustomer);
    
    return (
      <div className="min-h-screen bg-black text-white font-sans">
        {/* Header */}
        <div className="bg-black border-b border-[#333] px-4 py-4">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-2"
          >
            <ArrowLeft size={24} />
            <span className="text-base">Back to List</span>
          </button>
          <h1 className="text-2xl font-bold text-white">{selectedCustomer.name}</h1>
          {selectedCustomer.number && (
            <p className="text-white/70 text-base mt-1">{selectedCustomer.number}</p>
          )}
        </div>

        {/* Summary Card */}
        <div className="p-4">
          <div className="bg-black border border-[#333] rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <Wallet size={24} className="text-white" />
              <span className="text-base font-bold">Total Collection</span>
            </div>
            <p className="text-3xl font-bold">₹{totalAmount.toFixed(2)}</p>
            <p className="text-white/70 text-base mt-2">{selectedCustomer.history.length} transactions</p>
          </div>
        </div>

        {/* Payment History */}
        <div className="px-4 pb-6">
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-3">
            <Calendar size={24} className="text-white/70" />
            Payment History
          </h2>
          
          {selectedCustomer.history.length === 0 ? (
            <div className="bg-black border border-[#333] rounded-xl p-6 text-center">
              <p className="text-white/70 text-base">No payment history yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedCustomer.history.slice().reverse().map((record, index) => (
                <div
                  key={index}
                  className="bg-black border border-[#333] rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-full border border-[#333]">
                        <IndianRupee size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-base text-white">₹{record.amount}</p>
                        <p className="text-white/70 text-base">{record.mode}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-base font-medium text-white">{record.date}</p>
                      <p className="text-white/70 text-base">{record.time}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

// Update Modal Component
interface UpdateModalProps {
  customer: Customer;
  onClose: () => void;
  onSubmit: (amount: string, mode: string) => void;
}

function UpdateModal({ customer, onClose, onSubmit }: UpdateModalProps) {
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('Cash');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(amount, mode);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="card-bg rounded-2xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white">Update Collection</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-white text-lg font-medium mb-2">
              Customer: {customer.name}
            </label>
          </div>

          <div>
            <label className="block text-white text-lg font-medium mb-2">
              Amount
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <IndianRupee size={20} />
              </div>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg pl-12 pr-4 py-4 text-white min-text-22 focus:outline-none focus:border-green-500 transition-colors"
                placeholder="Enter amount"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-white text-lg font-medium mb-2">
              Payment Mode
            </label>
            <div className="flex flex-wrap gap-3">
              {['Cash', 'UPI', 'Bank'].map((paymentMode) => (
                <button
                  key={paymentMode}
                  type="button"
                  onClick={() => setMode(paymentMode)}
                  className={`px-6 py-3 rounded-xl min-text-22 font-medium transition-colors ${
                    mode === paymentMode
                      ? paymentMode === 'Cash' ? 'bg-green-600 text-white' :
                        paymentMode === 'UPI' ? 'bg-purple-600 text-white' :
                        'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {paymentMode}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-4 rounded-xl min-text-22 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!amount}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-800 disabled:text-gray-500 text-white py-4 rounded-xl min-text-22 font-medium transition-colors"
            >
              Add Collection
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default App;